import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useHistory, useLocation } from 'react-router-dom';
import SettingsIcon from '@material-ui/icons/Settings';

import quoteIconSrc from '@Calix-Commerce/design-system/assets/img/quote-icon.png';
import rmaIconSrc from '@Calix-Commerce/design-system/assets/img/rma-icon.png';
import orderIconSrc from '@Calix-Commerce/design-system/assets/img/order-icon.png';

import { StartRmaModal } from 'components/modals';
import { authenticationState, currentRmaState, accountState, checkoutState } from 'state/atoms';
import { ACCOUNT_MANAGER, VAR } from 'connectors/userTypes';
import { QUOTE, ORDER_MANAGER } from 'connectors/permissions';
import {
  CART_URL,
  CART_NEW_URL,
  QUOTE_LIST_URL,
  ORDER_MANAGER_URL,
  ACCOUNT_SEARCH_URL,
} from 'utils/constants';
import { getItemsPrice, getTotalCasePrice } from 'utils/helpers';
import { useIsCheckoutPage } from 'utils/hooks';

import { Menu, MenuDropdown, Title, MenuTotal, Account } from './styledComponents';
import { Option } from './Option';
import { RmaItemList } from './RmaItemList';
import { Feedback } from './Feedback';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';

const MainMenu = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNewRmaModalOpen, setIsNewRmaModalOpen] = useState(false);

  const history = useHistory();
  const { pathname } = useLocation();
  const isCheckoutPage = useIsCheckoutPage();

  const { account, userType, eCommerceAccess } = useRecoilValue(authenticationState);
  const currentRma = useRecoilValue(currentRmaState);
  const checkout = useRecoilValue(checkoutState);
  const accountData = useRecoilValue(accountState);
  const formatPrice = useFormatPrice();

  const handleNewCaseRedirect = () => {
    if (currentRma.items.length) {
      setIsNewRmaModalOpen(true);
    } else {
      history.push('/cases/new/search');
      setIsDropdownOpen(false);
    }
  };

  const handleCurrentCaseRedirect = () => {
    history.push('/cases/new/details');
    setIsDropdownOpen(false);
  };

  const getMenuPrice = () => {
    if (isCheckoutPage && pathname !== '/cases/new/checkout/shipping') {
      return getTotalCasePrice(
        currentRma.items,
        checkout.shipping.method,
        accountData.getDefaultBillToAddress,
        accountData.currencyConversionRate
      );
    } else {
      return getItemsPrice(currentRma.items);
    }
  };

  const showSettings = [ACCOUNT_MANAGER, VAR].some((type) => userType.includes(type));

  return (
    <Menu
      isOpen={isDropdownOpen}
      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      onClickOutside={() => setIsDropdownOpen(false)}
    >
      <Title>Current RMA</Title>
      <div>{currentRma.items.length} Items</div>
      <MenuTotal>{formatPrice(getMenuPrice(), currentRma.currencyCode)}</MenuTotal>
      <MenuDropdown propagateClick isOpen={isDropdownOpen} position="left">
        {showSettings && (
          <SettingsIcon onClick={() => window.location.assign(ACCOUNT_SEARCH_URL)} />
        )}
        <Account showSettings={showSettings}>{account}</Account>
        {eCommerceAccess.includes(QUOTE) && (
          <Option
            iconSrc={quoteIconSrc}
            text="Quotes"
            onClick={() => window.location.assign(QUOTE_LIST_URL)}
            onEyeClick={() => window.location.assign(CART_URL)}
            onPlusClick={() => window.location.assign(CART_NEW_URL)}
          />
        )}
        <Option
          selected
          iconSrc={rmaIconSrc}
          text="RMAs"
          onClick={() => history.push('/cases')}
          onEyeClick={handleCurrentCaseRedirect}
          onPlusClick={handleNewCaseRedirect}
        />
        {eCommerceAccess.includes(ORDER_MANAGER) && (
          <Option
            iconSrc={orderIconSrc}
            text="Orders"
            onClick={() => window.location.assign(ORDER_MANAGER_URL)}
          />
        )}
        {currentRma.items.length ? (
          <RmaItemList
            items={currentRma.items}
            caseNumber={currentRma.caseNumber}
            isCheckoutPage={isCheckoutPage}
            currencyCode={currentRma.currencyCode}
          />
        ) : null}
        <Feedback />
      </MenuDropdown>
      <StartRmaModal
        isOpen={isNewRmaModalOpen}
        onClose={() => setIsNewRmaModalOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }}
      />
    </Menu>
  );
};

export { MainMenu };
